import { useState } from 'react'
import { Link } from 'react-router-dom'

import { notierungenApi } from '../../api_notierungen'
import { useAktion, useLaden } from '../../hooks'
import Meldungen from '../../komponenten/Meldungen'
import { heuteIso, liefermonat, plusMonate, zahlLesen } from '../../format'
import { notierungLaufPfad } from '../../pfade'
import { NOTIERUNGSTATUS } from '../../texte'
import type { NotierungQuelle, NotierungStatus } from '../../typen'

interface Zeile {
  monat: string
  /** Text bis zum Speichern (Gestaltungsregel): gelesen wird erst beim Klick, mit Meldung je Fehler */
  preis: string
  status: NotierungStatus
}

/** Sechs Monate ab dem nächsten - so sehen die Angebote von Pöhner meist aus. */
function startzeilen(): Zeile[] {
  const erster = plusMonate(heuteIso(), 1)
  return Array.from({ length: 6 }, (_, i) => ({ monat: plusMonate(erster, i), preis: '', status: 'preis' as const }))
}

/**
 * Von Hand erfassen: eine Notierung, die nicht als Dokument kommt - Pöhner schickt sporadisch
 * Angebote, die Frau Geigenmüller abtippt. Spalte, Tag, je Liefermonat Preis oder „ausverkauft"/
 * „auf Anfrage". Wird gleich übernommen; der Lauf (Quelle „von Hand") lässt sich zurücknehmen.
 */
export default function ErfassenSeite() {
  const { daten: quellen, fehler: ladeFehler } = useLaden(() => notierungenApi.quellen(true), [])
  const [quelleId, setQuelleId] = useState<number | ''>('')
  const [tag, setTag] = useState(heuteIso())
  const [zeilen, setZeilen] = useState<Zeile[]>(startzeilen)
  const [bemerkung, setBemerkung] = useState('')
  const [gespeichert, setGespeichert] = useState<number | null>(null)
  const { fehler, erfolg, arbeitet, setFehler, ausfuehren } = useAktion()

  const monate = Array.from({ length: 24 }, (_, i) => plusMonate(heuteIso(), i - 1))

  function zeileSetzen(i: number, teil: Partial<Zeile>) {
    setZeilen((alt) => alt.map((z, j) => (j === i ? { ...z, ...teil } : z)))
  }

  function speichern() {
    if (quelleId === '') {
      setFehler('Bitte zuerst die Spalte wählen.')
      return
    }
    const werte: { liefermonat: string; preis: number | null; status: NotierungStatus }[] = []
    const fehlerListe: string[] = []
    for (const z of zeilen) {
      if (z.status !== 'preis') {
        werte.push({ liefermonat: z.monat, preis: null, status: z.status })
        continue
      }
      if (z.preis.trim() === '') continue
      const preis = zahlLesen(z.preis)
      if (preis === null) fehlerListe.push(`${liefermonat(z.monat)}: „${z.preis}“ ist keine Zahl.`)
      else if (preis < 100 || preis > 10000) fehlerListe.push(`${liefermonat(z.monat)}: ${z.preis} €/t ist kein Tonnenpreis (Tippfehler?).`)
      else werte.push({ liefermonat: z.monat, preis, status: 'preis' })
    }
    const doppelt = werte.map((w) => w.liefermonat).filter((m, i, a) => a.indexOf(m) !== i)
    if (doppelt.length > 0) fehlerListe.push(`${liefermonat(doppelt[0])} steht zweimal.`)
    if (fehlerListe.length > 0) {
      setFehler(fehlerListe.join(' '))
      return
    }
    if (werte.length === 0) {
      setFehler('Es ist noch kein Preis eingetragen.')
      return
    }
    void ausfuehren(async () => {
      const lauf = await notierungenApi.erfassen({ quelle_id: quelleId, notiert_am: tag, werte, bemerkung: bemerkung.trim() || null })
      setGespeichert(lauf.id)
      setZeilen(startzeilen())
      setBemerkung('')
    }, `${werte.length} Werte übernommen.`)
  }

  const gruppiert = new Map<string, NotierungQuelle[]>()
  for (const q of quellen ?? []) gruppiert.set(q.rohstoff, [...(gruppiert.get(q.rohstoff) ?? []), q])

  return (
    <>
      <h2>Von Hand erfassen</h2>
      <p className="hinweis" style={{ maxWidth: '75ch' }}>
        Für Angebote, die nicht als Dokument kommen - etwa von Pöhner am Telefon. Die Werte werden sofort übernommen und
        lassen sich über den entstandenen Lauf zurücknehmen. Eine eingefügte Mail geht schneller unter Läufe.
      </p>
      <Meldungen fehler={ladeFehler ?? fehler} erfolg={erfolg} />
      {gespeichert !== null && (
        <p>
          <Link to={notierungLaufPfad(gespeichert)}>Lauf {gespeichert} ansehen</Link>
        </p>
      )}
      <div className="karte">
        <div className="leiste felder">
          <label className="feld">
            <span className="hinweis">Spalte</span>
            <select value={quelleId} onChange={(e) => setQuelleId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">- bitte wählen -</option>
              {[...gruppiert.entries()].map(([rohstoff, liste]) => (
                <optgroup key={rohstoff} label={rohstoff}>
                  {liste.map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.bezeichnung}
                      {q.qualitaet ? ` (${q.qualitaet})` : ''}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          <label className="feld">
            <span className="hinweis">Tag der Notierung</span>
            <input type="date" value={tag} max={heuteIso()} onChange={(e) => setTag(e.target.value)} />
          </label>
        </div>
        <div className="tabellenrahmen">
          <table className="nz-erfassen">
            <thead>
              <tr>
                <th>Liefermonat</th>
                <th className="zahl">Preis €/t netto</th>
                <th>Stand</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {zeilen.map((z, i) => (
                <tr key={i}>
                  <td>
                    <select value={z.monat} onChange={(e) => zeileSetzen(i, { monat: e.target.value })}>
                      {monate.map((m) => (
                        <option key={m} value={m}>
                          {liefermonat(m)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="zahl">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={z.preis}
                      disabled={z.status !== 'preis'}
                      placeholder={i === 0 && z.status === 'preis' ? '1.350,00' : ''}
                      onChange={(e) => zeileSetzen(i, { preis: e.target.value })}
                    />
                  </td>
                  <td>
                    <select value={z.status} onChange={(e) => zeileSetzen(i, { status: e.target.value as NotierungStatus })}>
                      {(Object.keys(NOTIERUNGSTATUS) as NotierungStatus[]).map((s) => (
                        <option key={s} value={s}>
                          {NOTIERUNGSTATUS[s]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button className="leise" onClick={() => setZeilen(zeilen.filter((_, j) => j !== i))}>
                      entfernen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="leiste" style={{ marginTop: 8 }}>
          <button
            onClick={() =>
              setZeilen([...zeilen, { monat: plusMonate(zeilen[zeilen.length - 1]?.monat ?? heuteIso(), 1), preis: '', status: 'preis' }])
            }
          >
            Monat dazu
          </button>
        </div>
        <label className="feld" style={{ width: '100%' }}>
          <span className="hinweis">Bemerkung (optional)</span>
          <input type="text" value={bemerkung} onChange={(e) => setBemerkung(e.target.value)} placeholder="Angebot telefonisch, gültig bis Freitag" />
        </label>
        <div className="leiste" style={{ marginTop: 12, marginBottom: 0 }}>
          <button className="haupt" disabled={arbeitet} onClick={speichern}>
            Übernehmen
          </button>
          <span className="hinweis">Leere Preiszeilen werden weggelassen.</span>
        </div>
      </div>
    </>
  )
}
